import { useUtils } from "../../utils/utils"

import { reactive } from "vue"

import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer"
import { executeQueryJSON } from "@arcgis/core/rest/query"
import Query from "@arcgis/core/rest/support/Query"
import Extent from "@arcgis/core/geometry/Extent"
import Graphic from "@arcgis/core/Graphic"
import Point from "@arcgis/core/geometry/Point"
import type MultiPoint from "@arcgis/core/geometry/Multipoint"
import Polygon from "@arcgis/core/geometry/Polygon"
import PointSymbol3D from "@arcgis/core/symbols/PointSymbol3D"
import LineSymbol3D from "@arcgis/core/symbols/LineSymbol3D"
import PolygonSymbol3D from "@arcgis/core/symbols/PolygonSymbol3D"
import IconSymbol3DLayer from "@arcgis/core/symbols/IconSymbol3DLayer"
import {
    load as projectionLoad,
    isLoaded as projectionIsLoaded,
    execute as project,
} from "@arcgis/core/geometry/operators/projectOperator"
import { execute as intersects } from "@arcgis/core/geometry/operators/intersectsOperator"
import GeographicTransformationStep from "@arcgis/core/geometry/operators/support/GeographicTransformationStep"
import GeographicTransformation from "@arcgis/core/geometry/operators/support/GeographicTransformation"
import * as convexHullOperator from "@arcgis/core/geometry/operators/convexHullOperator"
import SpatialReference from "@arcgis/core/geometry/SpatialReference"
import Camera from "@arcgis/core/Camera"

import type { SearchTypes } from "../../types/search"
import type { GeometryUnion } from "@arcgis/core/geometry/types"

export type AcceptedTools = "point" | "polyline" | "polygon"

const { getMapRight, getView, getViewRight } = useUtils()

const GRAPHICS_LAYER_ID = "searchGraphics"
let graphicsLayer: GraphicsLayer | undefined = undefined
const query = new Query()
query.returnGeometry = true

const MODEL_BOUNDARY_GEOJSON = new Polygon({
    rings: [
        [
            [-744371.36300000176, -1042942.2424999997],
            [-744364.32200000063, -1042923.7474999987],
            [-744356.09430000186, -1042924.5953000002],
            [-744325.92500000075, -1042935.5936999992],
            [-744314.44799999893, -1042941.5637999997],
            [-744255.18299999833, -1042964.0590000004],
            [-744244.64130000025, -1042969.3520000018],
            [-744198.63179999962, -1042983.7391000018],
            [-744201.56439999864, -1042982.7853000015],
            [-744192.95089999959, -1042987.2140000015],
            [-744192.01319999993, -1042996.5031000003],
            [-744194.62530000135, -1043027.3522999994],
            [-744192.82380000129, -1043029.2943000011],
            [-744169.31419999897, -1043037.5377000012],
            [-744174.39310000092, -1043174.6612000018],
            [-744137.6325000003, -1043181.0262999982],
            [-744140.49179999903, -1043197.0778999999],
            [-744129.72920000181, -1043197.4206000008],
            [-744127.41090000048, -1043221.9890000001],
            [-744126.9748999998, -1043241.4114999995],
            [-744129.12269999832, -1043245.4048999995],
            [-744141.36969999969, -1043252.6950000003],
            [-744152.3553000018, -1043254.8202],
            [-744154.19700000063, -1043304.9277000017],
            [-744156.86309999973, -1043319.0571000017],
            [-744155.71570000052, -1043335.1948000006],
            [-744154.17590000108, -1043339.1433000006],
            [-744157.15069999918, -1043364.6640000008],
            [-744149.88329999894, -1043377.0896999985],
            [-744149.37260000035, -1043399.5058999993],
            [-744148.4833999984, -1043400.5749000013],
            [-744126.94060000032, -1043399.4153999984],
            [-744128.90569999814, -1043401.5243999995],
            [-744134.3264000006, -1043554.6682999991],
            [-744132.43459999934, -1043555.184700001],
            [-744132.52490000054, -1043556.6103000008],
            [-744129.22819999978, -1043556.7120000012],
            [-744132.42529999837, -1043599.1262999997],
            [-744133.47140000015, -1043599.189100001],
            [-744140.91120000184, -1043668.5102999993],
            [-744058.15419999883, -1043677.6532000005],
            [-743884.05409999937, -1043693.4618000016],
            [-743876.16180000082, -1043711.1638999991],
            [-743744.0848999992, -1043686.6832000017],
            [-743663.98930000141, -1043669.2591999993],
            [-743553.30260000005, -1043648.0230999999],
            [-743513.39959999919, -1043634.1589000002],
            [-743514.0689000003, -1043640.624499999],
            [-743508.29080000147, -1043644.9989],
            [-743465.11080000177, -1043651.5124000013],
            [-743438.58469999954, -1043651.1992000006],
            [-743432.51689999923, -1043653.1024999991],
            [-743432.94929999858, -1043661.1041000001],
            [-743413.46880000085, -1043661.1541999988],
            [-743402.95710000023, -1043662.3861000016],
            [-743399.89589999989, -1043664.8361000009],
            [-743377.96059999987, -1043662.1348999999],
            [-743369.13199999928, -1043659.5546999983],
            [-743363.18290000036, -1043659.8858999982],
            [-743349.25989999995, -1043656.2740000002],
            [-743339.04300000146, -1043655.6979999989],
            [-743325.06509999931, -1043658.5251000002],
            [-743301.67810000107, -1043649.5190000013],
            [-743280.28280000016, -1043647.9576000012],
            [-743275.23220000044, -1043645.1402999982],
            [-743257.40199999884, -1043641.8759000003],
            [-743225.92350000143, -1043629.1352000013],
            [-743211.29019999877, -1043636.5738999993],
            [-743177.0449000001, -1043624.3762000017],
            [-743168.55359999835, -1043623.6466999985],
            [-743149.22740000114, -1043615.7641000003],
            [-743138.07449999824, -1043617.8449999988],
            [-743123.98550000042, -1043615.2377999984],
            [-743109.01260000095, -1043609.3436000012],
            [-743106.56359999999, -1043610.3533000015],
            [-743096.23160000145, -1043608.7976999991],
            [-743026.75739999861, -1043580.6292999983],
            [-743013.5036999993, -1043586.1636000015],
            [-742911.19649999961, -1043563.6873000003],
            [-742831.4635999985, -1043541.5786000006],
            [-742805.91340000182, -1043527.1226999983],
            [-742788.66349999979, -1043514.2050999999],
            [-742794.75360000134, -1043507.1077999994],
            [-742788.66349999979, -1043514.2050999999],
            [-742782.31960000098, -1043510.3150999993],
            [-742709.86250000075, -1043453.4090999998],
            [-742672.09329999983, -1043418.7580999993],
            [-742602.19970000163, -1043363.8018000014],
            [-742553.76960000023, -1043323.0711000003],
            [-742454.71379999816, -1043256.1983000003],
            [-742442.36270000041, -1043250.8223999999],
            [-742403.77600000054, -1043224.7567999996],
            [-742392.07019999996, -1043210.6532000005],
            [-742376.77600000054, -1043201.4221000001],
            [-742369.38729999959, -1043189.508200001],
            [-742365.14249999821, -1043188.7969000004],
            [-742347.71279999986, -1043178.6669000015],
            [-742322.09090000018, -1043156.6944000013],
            [-742302.61569999903, -1043133.4822999984],
            [-742301.18510000035, -1043129.9470999986],
            [-742288.54800000042, -1043126.9136999995],
            [-742286.16230000183, -1043124.7503000014],
            [-742259.07169999927, -1043082.5172999986],
            [-742245.14750000089, -1043053.8621999994],
            [-742233.58700000122, -1043015.0980999991],
            [-742227.65049999952, -1043003.4508999996],
            [-742225.47360000014, -1042984.9362000003],
            [-742221.57299999893, -1042983.6994000003],
            [-742220.47830000147, -1042980.4312999994],
            [-742210.03299999982, -1042941.6570999995],
            [-742202.68079999834, -1042889.4822999984],
            [-742205.7846999988, -1042782.0837000012],
            [-742215.93640000001, -1042696.6468000002],
            [-742223.92660000175, -1042528.9468999989],
            [-742231.80429999903, -1042444.3590999991],
            [-742228.32499999925, -1042445.5064999983],
            [-742223.73000000045, -1042400.3817000017],
            [-742268.86630000174, -1042391.5166000016],
            [-742359.60119999945, -1042359.5901999995],
            [-742420.19440000132, -1042343.2129999995],
            [-742480.70059999824, -1042324.368900001],
            [-742571.6026000008, -1042301.6334000006],
            [-742632.35660000145, -1042292.6658000015],
            [-742693.08570000157, -1042286.0762000009],
            [-742753.75580000132, -1042275.5322999991],
            [-742845.690200001, -1042272.7991000004],
            [-742910.2424999997, -1042267.6510999985],
            [-742942.68780000135, -1042268.3286000006],
            [-743051.31080000103, -1042263.1209999993],
            [-743050.36369999871, -1042270.4312000014],
            [-743059.53730000183, -1042273.8729999997],
            [-743062.21240000054, -1042264.4279999994],
            [-743077.57629999891, -1042231.7818000019],
            [-743081.40159999952, -1042231.3425999992],
            [-743194.98149999976, -1042279.3443999998],
            [-743271.55689999834, -1042314.3051000014],
            [-743276.49509999901, -1042312.2820999995],
            [-743430.35130000114, -1042314.8304000013],
            [-743535.43840000033, -1042312.6099000014],
            [-743545.97830000147, -1042309.8907000013],
            [-743574.58839999884, -1042296.8533000015],
            [-743594.6651000008, -1042294.9684999995],
            [-743596.12889999896, -1042295.3332000002],
            [-743602.27360000089, -1042318.2463000007],
            [-743748.44460000098, -1042328.8031000011],
            [-743792.89530000091, -1042327.9178000018],
            [-743820.54100000113, -1042329.121199999],
            [-743820.38300000131, -1042332.6944999993],
            [-743835.9646999985, -1042324.7796999998],
            [-743839.45690000057, -1042325.0738000013],
            [-743840.52299999818, -1042297.9450000003],
            [-743839.36059999838, -1042297.5535000004],
            [-743837.56989999861, -1042268.5119999982],
            [-743838.66290000081, -1042252.7749999985],
            [-743835.31109999865, -1042245.8471000008],
            [-743840.06469999999, -1042246.2259000018],
            [-743835.31109999865, -1042245.8471000008],
            [-743838.16620000079, -1042149.2798999995],
            [-743973.77800000086, -1042157.2124999985],
            [-744184.55150000006, -1042173.4063000008],
            [-744184.88589999825, -1042184.6647000015],
            [-744184.55150000006, -1042173.4063000008],
            [-744258.51610000059, -1042180.9822000004],
            [-744267.00050000101, -1042184.0879000016],
            [-744313.30469999835, -1042189.4719000012],
            [-744363.09430000186, -1042198.6042000018],
            [-744365.60330000147, -1042204.6193000004],
            [-744400.56410000101, -1042172.0095999986],
            [-744413.60990000144, -1042170.7501999997],
            [-744476.64359999821, -1042171.6763999984],
            [-744552.15630000085, -1042175.1552000009],
            [-744573.61270000041, -1042193.6376999989],
            [-744625.26110000163, -1042244.4756999984],
            [-744619.71829999983, -1042249.1261],
            [-744628.42399999872, -1042245.4811999984],
            [-744663.19799999893, -1042282.2793999985],
            [-744665.19530000165, -1042282.002700001],
            [-744703.40150000155, -1042347.9934],
            [-744707.50890000165, -1042357.0317000002],
            [-744709.47929999977, -1042366.0672999993],
            [-744714.84829999879, -1042367.6633000001],
            [-744764.65419999883, -1042359.6695999987],
            [-744857.88729999959, -1042323.9574999996],
            [-744908.7762000002, -1042282.0142999999],
            [-744923.55440000072, -1042278.2250999995],
            [-744932.07110000029, -1042290.2366999984],
            [-744935.81430000067, -1042322.0742000006],
            [-744935.33260000125, -1042352.5056999996],
            [-744936.85289999843, -1042365.4365999997],
            [-744935.03750000149, -1042375.8925999999],
            [-744932.15500000119, -1042446.3299999982],
            [-744941.21240000054, -1042531.998399999],
            [-744944.24060000107, -1042531.8577000014],
            [-744946.37489999831, -1042541.3784000017],
            [-744955.0960999988, -1042606.9232000001],
            [-744947.64510000125, -1042612.6075000018],
            [-744953.86499999836, -1042613.7162000015],
            [-744954.92770000175, -1042622.7551000006],
            [-744982.8610999994, -1042635.9012999982],
            [-744982.39090000093, -1042666.4307999983],
            [-744980.59629999846, -1042668.431499999],
            [-744936.93329999968, -1042668.2470000014],
            [-744792.35940000042, -1042664.3682999983],
            [-744731.52580000088, -1042664.8894999996],
            [-744695.4395999983, -1042660.0914000012],
            [-744696.19519999996, -1042658.9778999984],
            [-744683.06760000065, -1042658.4499999993],
            [-744634.71429999918, -1042661.5333999991],
            [-744603.24700000137, -1042661.4915000014],
            [-744599.9422999993, -1042659.2811000012],
            [-744599.70910000056, -1042657.2976000011],
            [-744570.05730000138, -1042655.4321000017],
            [-744566.52120000124, -1042659.8852000013],
            [-744563.69700000063, -1042679.0258999988],
            [-744560.37510000169, -1042685.3000999987],
            [-744551.84189999849, -1042716.3092],
            [-744540.29560000077, -1042747.5863000005],
            [-744538.47670000046, -1042758.0130000003],
            [-744528.60979999974, -1042786.309700001],
            [-744526.85779999942, -1042796.1231999993],
            [-744564.86510000005, -1042809.8110000007],
            [-744557.0364000015, -1042898.6048000008],
            [-744528.18490000069, -1042903.5467999987],
            [-744485.85909999907, -1042914.7355999984],
            [-744462.82669999823, -1042917.3781999983],
            [-744455.13699999824, -1042921.0318000019],
            [-744423.72140000015, -1042930.0623000003],
            [-744371.36300000176, -1042942.2424999997],
        ],
    ],
})

const symbols = {
    default: {
        point: new PointSymbol3D({
            symbolLayers: [
                new IconSymbol3DLayer({
                    size: 18,
                    material: { color: [163, 20, 36] },
                    outline: {
                        size: 2,
                        color: [50, 50, 50, 0.5],
                    },
                }),
            ],
            verticalOffset: {
                screenLength: 50,
                maxWorldLength: 100,
                minWorldLength: 35,
            },
            callout: {
                type: "line", // autocasts as new LineCallout3D()
                color: "black",
                size: 1,
            },
        }),
        polyline: new LineSymbol3D({
            symbolLayers: [
                {
                    type: "path", // autocasts as new PathSymbol3DLayer()
                    profile: "quad", // creates a rectangular shape
                    width: 5, // path width in meters
                    height: 20, // path height in meters
                    material: { color: [163, 20, 36, 0.8] },
                    cap: "square",
                    profileRotation: "heading",
                },
            ],
        }),
        polygon: new PolygonSymbol3D({
            symbolLayers: [
                {
                    type: "fill", // autocasts as new FillSymbol3DLayer()
                    material: { color: [163, 20, 36, 0.4] },
                    outline: {
                        color: [163, 20, 36, 1],
                        size: 4,
                    },
                },
            ],
        }),
    },
    highlight: {
        point: new PointSymbol3D({
            symbolLayers: [
                new IconSymbol3DLayer({
                    size: 20,
                    material: { color: [0, 175, 240, 0.8] },
                    outline: {
                        size: 2,
                        color: [50, 50, 50, 0.5],
                    },
                }),
            ],
            verticalOffset: {
                screenLength: 50,
                maxWorldLength: 100,
                minWorldLength: 35,
            },
            callout: {
                type: "line", // autocasts as new LineCallout3D()
                color: "black",
                size: 1,
            },
        }),
        polyline: new LineSymbol3D({
            symbolLayers: [
                {
                    type: "path", // autocasts as new PathSymbol3DLayer()
                    profile: "quad", // creates a rectangular shape
                    width: 5, // path width in meters
                    height: 20, // path height in meters
                    material: { color: [0, 175, 240, 0.8] },
                    cap: "square",
                    profileRotation: "heading",
                },
            ],
        }),
        polygon: new PolygonSymbol3D({
            symbolLayers: [
                {
                    type: "fill", // autocasts as new FillSymbol3DLayer()
                    material: { color: [0, 175, 240, 0.4] },
                    outline: {
                        color: [0, 175, 240, 1],
                        size: 4,
                    },
                },
            ],
        }),
    },
}

export const state = reactive<{
    results: Graphic[]
    isFetching: boolean
    hasResults: boolean
    outsideBoundary: boolean
    resultsType: SearchTypes | null
}>({
    results: [],
    isFetching: false,
    hasResults: false,
    outsideBoundary: false,
    resultsType: null,
})

function zoomPolygones(graphics: Graphic[] | Graphic[][]) {
    const view = getViewRight()
    let extent: Extent | undefined = undefined // extent of all graphics
    let graphicExtent: Extent
    const spatialReference = view.spatialReference

    for (let i = 0; i < graphics.length; i++) {
        const graphic = graphics[i] as Graphic | Graphic[]

        if (Array.isArray(graphic)) {
            for (let j = 0; j < graphic.length; j++) {
                const graphicJ = graphic[j] as Graphic
                if (!graphicJ.geometry) continue
                if (graphicJ.geometry?.type == "point") {
                    graphicExtent = new Extent({
                        xmin: (graphicJ.geometry as Point).x - 100,
                        xmax: (graphicJ.geometry as Point).x + 100,
                        ymin: (graphicJ.geometry as Point).y - 100,
                        ymax: (graphicJ.geometry as Point).y + 100,
                        spatialReference,
                    })
                } else if (graphicJ.geometry?.type == "multipoint") {
                    const point0 = (graphicJ.geometry as MultiPoint)
                        .points[0] as [number, number]
                    graphicExtent = new Extent({
                        xmin: point0[0] - 100,
                        xmax: point0[0] + 100,
                        ymin: point0[1] - 100,
                        ymax: point0[1] + 100,
                        spatialReference,
                    })
                } else {
                    graphicExtent = (graphicJ.geometry as GeometryUnion)
                        .extent as Extent
                }

                if (!extent) {
                    extent = graphicExtent
                } else {
                    extent = extent.union(graphicExtent)
                }
            }
        } else {
            if (graphic.geometry?.type === "point") {
                graphicExtent = new Extent({
                    xmin: (graphic.geometry as Point).x - 100,
                    xmax: (graphic.geometry as Point).x + 100,
                    ymin: (graphic.geometry as Point).y - 100,
                    ymax: (graphic.geometry as Point).y + 100,
                    spatialReference,
                })
            } else if (graphic.geometry?.type === "multipoint") {
                const point0 = (graphic.geometry as MultiPoint).points[0] as [
                    number,
                    number,
                ]
                graphicExtent = new Extent({
                    xmin: point0[0] - 100,
                    xmax: point0[0] + 100,
                    ymin: point0[1] - 100,
                    ymax: point0[1] + 100,
                    spatialReference,
                })
            } else {
                graphicExtent = graphic.geometry?.extent as Extent
            }

            if (!extent) {
                extent = graphicExtent
            } else {
                extent = extent.union(graphicExtent)
            }
        }
    }

    const expandFactor = 2
    const newExtent = extent?.clone().expand(expandFactor)
    if (newExtent) {
        view.extent = newExtent
    }
}

function getSymbol(geometryType: AcceptedTools) {
    if (!symbols) return null
    return symbols.default[geometryType]
}

function getGraphicByAttribute(idField: string, id: number) {
    const graphics = graphicsLayer?.graphics
    const mergedGraphics: Graphic[] = []

    graphics?.forEach(graphic => {
        const attributes = graphic.attributes
        if (!attributes[idField]) return

        if (
            attributes[idField].toString().toLowerCase() ==
            id.toString().toLowerCase()
        ) {
            mergedGraphics.push(graphic)
        }
    })
    return mergedGraphics
}

export function useSearchLogic() {
    function init() {
        if (getMapRight().findLayerById(GRAPHICS_LAYER_ID)) {
            return
        }
        graphicsLayer = new GraphicsLayer({
            id: GRAPHICS_LAYER_ID,
        })
        getMapRight().add(graphicsLayer)
    }

    async function run(params: {
        searchType: SearchTypes
        whereClause: string
        outFields: string[]
        queryUrl: string
        suggestions?: boolean
    }) {
        return run3D(params)
    }

    async function run3D(params: Parameters<typeof run>[0]) {
        clear()
        state.resultsType = params.searchType
        state.hasResults = false
        state.isFetching = true
        query.outFields = params.outFields
        query.where = params.whereClause

        try {
            const result = await executeQueryJSON(params.queryUrl, query)
            state.isFetching = false
            if (!params.suggestions) {
                process3DSearchResults(result.features)
                state.hasResults = true
            }
            return result
        } catch (err) {
            state.isFetching = false
            console.log(err)
        }
    }

    async function process3DSearchResults(features: Graphic[]) {
        graphicsLayer?.removeAll()
        state.outsideBoundary = false
        state.results = []
        state.hasResults = false

        const resultGeometries: GeometryUnion[] = []

        if (features.length > 0) {
            features.forEach(async item => {
                const intersectsModelBoundary = intersects(
                    item.geometry as GeometryUnion,
                    MODEL_BOUNDARY_GEOJSON,
                )
                if (!intersectsModelBoundary) {
                    state.outsideBoundary = true
                    state.hasResults = true
                    return
                }
                if (!projectionIsLoaded()) {
                    await projectionLoad()
                }
                if (!item.geometry) return
                const type = item.geometry.type
                const step = new GeographicTransformationStep({
                    wkid: 1623,
                })

                // Create the full transformation
                const transformation = new GeographicTransformation({
                    steps: [step],
                })
                const projectedGeometry = project(
                    item.geometry,
                    new SpatialReference({ wkid: 3857 }),
                    {
                        geographicTransformation: transformation,
                    },
                )
                const outGraphic = new Graphic({
                    geometry: projectedGeometry,
                    attributes: item.attributes,
                    symbol: getSymbol(type as AcceptedTools),
                })
                state.hasResults = true
                if (!outGraphic.geometry) return
                resultGeometries.push(outGraphic.geometry)
                graphicsLayer?.add(outGraphic)
            })
            if (resultGeometries.length === 0) {
                return
            }
            const convex = convexHullOperator.executeMany(resultGeometries, {
                merge: true,
            })
            if (convex[0]?.extent?.center) {
                goTo(convex[0]?.extent.center as Point)
            }
        } else {
            state.hasResults = true
            state.results = []
        }
    }

    function goTo(point: Point, type?: string) {
        const z = type === "point" ? 500 : type === "polyline" ? 3000 : 1500
        const camera = new Camera({
            position: new Point({
                longitude: point.longitude,
                latitude: point.latitude,
                z,
            }),
            tilt: 0,
            heading: 0,
        })
        getViewRight().goTo(camera)
        getView().goTo(camera)
    }

    function clear() {
        state.results = [] // empty const array
        graphicsLayer?.removeAll()
        state.hasResults = false
    }

    function displayGraphics(graphics: Graphic[]) {
        if (graphics.length === 0) {
            return
        }

        for (let i = 0; i < graphics.length; i++) {
            //ArcGIS API bug with Vue: accessor class cannot be made reactive in Vue
            //accessor are stored frozen in reactive array
            //to change their symbols in runtime we need to convert them to json and back to accessor
            const graphicJSON = (graphics[i] as Graphic).toJSON()
            const graphic = Graphic.fromJSON(graphicJSON)
            const geometryType = graphic.geometry.type as AcceptedTools
            const symbol = getSymbol(geometryType)
            if (symbol) {
                graphic.symbol = symbol
            }
            graphicsLayer?.add(graphic)
        }

        zoomPolygones(graphics)
    }

    function highlightGraphic(idField: string, id: number) {
        const graphic = getGraphicByAttribute(idField, id)

        for (let i = 0; i < graphic.length; i++) {
            setSymbol(graphic[i] as Graphic, "highlight")
        }
    }

    function removeHighlight(idField: string, id: number) {
        const graphic = getGraphicByAttribute(idField, id)

        for (let i = 0; i < graphic.length; i++) {
            setSymbol(graphic[i] as Graphic, "default")
        }
    }

    function setSymbol(graphic: Graphic, symbolType: "default" | "highlight") {
        if (!symbols) return
        const geometryType = graphic.geometry?.type
        graphic.symbol = symbols[symbolType][geometryType as AcceptedTools]
    }

    function zoomToGeometries(geometries: Graphic[]) {
        zoomPolygones(geometries)
    }

    function zoomGraphic(idField: string, id: number) {
        const graphic = getGraphicByAttribute(idField, id)
        zoomPolygones([graphic])
    }

    function toggleGraphic(idField: string, id: number) {
        const graphic = getGraphicByAttribute(idField, id)
        graphic.forEach(item => {
            item.visible = !item.visible
        })
    }

    function removeGraphic(idField: string, id: number) {
        const graphic = getGraphicByAttribute(idField, id)

        for (let i = 0; i < graphic.length; i++) {
            graphicsLayer?.remove(graphic[i] as Graphic)
        }
        const idx = state.results.findIndex(
            item => item.attributes[idField] == id,
        )
        state.results.splice(idx, 1)

        if (state.results.length === 0) {
            state.hasResults = false
        }
    }

    function upperCaseFirstLetter(string: string) {
        return string.charAt(0).toUpperCase() + string.slice(1)
    }

    function lowerCaseAllWordsExceptFirstLetters(string: string) {
        return string.replace(/\w\S*/g, function (word) {
            return word.charAt(0) + word.slice(1).toLowerCase()
        })
    }

    return {
        state,
        init,
        clear,
        run,
        run3D,
        displayGraphics,
        zoomToGeometries,
        zoomGraphic,
        removeGraphic,
        toggleGraphic,
        highlightGraphic,
        removeHighlight,
        upperCaseFirstLetter,
        lowerCaseAllWordsExceptFirstLetters,
    }
}
